import { supabaseClient } from '../supabase';
import { logger } from '../logger';
import { HOTEL_ID } from '../supabase/configClient';
import { v4 as uuidv4 } from 'uuid';
import type { SupportedLanguages } from '../../i18n';

interface SessionData {
  id: string;
  hotel_id: string;
  anonymous_id: string;
  start_time: string;
  device_type?: string;
  browser?: string;
  os?: string;
  screen_size?: string;
  is_pwa: boolean;
  is_returning_user: boolean;
  referrer?: string;
  language?: string;
  is_active: boolean;
  page_views: number;
  interactions: number;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

class SessionTracker {
  private static instance: SessionTracker;
  private sessionId: string | null = null;
  private anonymousId: string | null = null;
  private sessionData: SessionData | null = null;
  private pageViews: number = 0;
  private interactions: number = 0;
  private isInitialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): SessionTracker {
    if (!SessionTracker.instance) {
      SessionTracker.instance = new SessionTracker();
    }
    return SessionTracker.instance;
  }

  private async initialize(): Promise<void> {
    if (this.isInitialized || this.initPromise) {
      return this.initPromise || Promise.resolve();
    }

    this.initPromise = (async () => {
      try {
        this.anonymousId = this.getOrCreateAnonymousId();
        await this.createSession();
        this.isInitialized = true;
      } catch (error) {
        logger.error('Error initializing session tracker', {
          category: 'Analytics',
          data: { error }
        });
        this.isInitialized = false;
      } finally {
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  private getOrCreateAnonymousId(): string {
    try {
      let anonymousId = localStorage.getItem('anonymous_id');
      if (!anonymousId) {
        anonymousId = uuidv4();
        localStorage.setItem('anonymous_id', anonymousId);
      }
      return anonymousId;
    } catch (error) {
      logger.error('Error accessing anonymous ID in localStorage', {
        category: 'Analytics',
        data: { error }
      });
      return uuidv4();
    }
  }

  private async createSession(): Promise<void> {
    try {
      this.sessionId = uuidv4();

      this.sessionData = {
        id: this.sessionId,
        hotel_id: HOTEL_ID,
        anonymous_id: this.anonymousId!,
        start_time: new Date().toISOString(),
        device_type: this.getDeviceType(),
        browser: this.getBrowser(),
        os: this.getOS(),
        screen_size: this.getScreenSize(),
        is_pwa: this.isPWA(),
        is_returning_user: this.isReturningUser(),
        referrer: document.referrer || undefined,
        language: navigator.language,
        is_active: true,
        page_views: 1,
        interactions: 0
      };

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('utm_source')) this.sessionData.utm_source = urlParams.get('utm_source') || undefined;
      if (urlParams.has('utm_medium')) this.sessionData.utm_medium = urlParams.get('utm_medium') || undefined;
      if (urlParams.has('utm_campaign')) this.sessionData.utm_campaign = urlParams.get('utm_campaign') || undefined;

      const { error } = await supabaseClient
        .from('sessions')
        .insert([this.sessionData]);

      if (error) throw error;

      this.pageViews = 1;
      this.interactions = 0;

      window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    } catch (error) {
      logger.error('Error creating session', {
        category: 'Analytics',
        data: { error }
      });
      throw error;
    }
  }

  private async updateSession(): Promise<void> {
    if (!this.sessionId || !this.sessionData) {
      logger.warn('Attempted to update uninitialized session', { category: 'Analytics' });
      return;
    }

    try {
      this.sessionData.page_views = this.pageViews;
      this.sessionData.interactions = this.interactions;

      const { error } = await supabaseClient
        .from('sessions')
        .update({
          page_views: this.pageViews,
          interactions: this.interactions,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.sessionId);

      if (error) throw error;
    } catch (error) {
      logger.error('Error updating session', {
        category: 'Analytics',
        data: { error }
      });
    }
  }

  private async handleBeforeUnload(): Promise<void> {
    if (!this.sessionId || !this.sessionData) return;

    try {
      const startTime = new Date(this.sessionData.start_time).getTime();
      const duration = Math.floor((Date.now() - startTime) / 1000);

      await supabaseClient
        .from('sessions')
        .update({
          is_active: false,
          duration,
          end_reason: 'page_close',
          page_views: this.pageViews,
          interactions: this.interactions,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.sessionId);
    } catch {
      // beforeunload cannot await async operations reliably
    }
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return 'mobile';
    return 'desktop';
  }

  private getBrowser(): string {
    const ua = navigator.userAgent;
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("SamsungBrowser")) return "Samsung Browser";
    if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
    if (ua.includes("Edg")) return "Edge";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Safari")) return "Safari";
    return "Unknown";
  }

  private getOS(): string {
    const ua = navigator.userAgent;
    if (/Windows/.test(ua)) return "Windows";
    if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
    if (/Android/.test(ua)) return "Android";
    if (/Mac OS X/.test(ua)) return "macOS";
    if (/Linux/.test(ua)) return "Linux";
    return "Unknown";
  }

  private getScreenSize(): string {
    return `${window.screen.width}x${window.screen.height}`;
  }

  private isPWA(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (navigator as any).standalone === true;
  }

  private isReturningUser(): boolean {
    try {
      return localStorage.getItem('has_visited') === 'true';
    } catch {
      return false;
    }
  }

  public getSessionId(): string | null {
    return this.sessionId;
  }

  public getAnonymousId(): string | null {
    return this.anonymousId;
  }

  public incrementPageView(): void {
    this.pageViews++;
    if (this.pageViews % 5 === 0) this.updateSession();
  }

  public incrementInteraction(): void {
    this.interactions++;
    if (this.interactions % 10 === 0) this.updateSession();
  }

  public updateLanguage(language: SupportedLanguages): void {
    if (!this.sessionId || !this.sessionData) return;

    try {
      this.sessionData.language = language;
      supabaseClient
        .from('sessions')
        .update({ language, updated_at: new Date().toISOString() })
        .eq('id', this.sessionId)
        .then(({ error }) => {
          if (error) throw error;
        })
        .catch(error => {
          logger.error('Error updating session language', {
            category: 'Analytics',
            data: { error }
          });
        });
    } catch (error) {
      logger.error('Error updating session language', {
        category: 'Analytics',
        data: { error }
      });
    }
  }
}

export const sessionTracker = SessionTracker.getInstance();
